import { Component, OnDestroy, OnInit, inject } from "@angular/core";
import { Router } from "@angular/router";
import { LegalRecordStateService } from "@core/services/utils/legal-record-state.service";
import { processHttpError } from "@core/services/utils/process-http-error";
import { TranslatePipe } from "@ngx-translate/core";
import { LegalRecordUI } from "@services/business/_models/interfaces/record.interfaces";
import { LCEventEnum } from "@services/ui-helpers/_models/interfaces/messaging.interfaces";
import {
  NotificationMessage,
  NotificationMessageReceiver,
} from "@services/ui-helpers/_models/interfaces/notifications.interfaces";
import { BENotificationsService } from "@services/ui-helpers/notifications-services/be-notifications.service";
import { IconDisplayComponent } from "@ui/ui-helpers/display-formats/icon-display/icon-display.component";
import { Subject, takeUntil } from "rxjs";

@Component({
  selector: "extra-info-header",
  templateUrl: "./extra-info-header.component.html",
  styleUrl: "./extra-info-header.component.scss",
  imports: [IconDisplayComponent, TranslatePipe],
})
export class ExtraInfoHeaderComponent
  implements OnInit, OnDestroy, NotificationMessageReceiver
{
  private readonly router = inject(Router);
  private readonly legalRecordStateService = inject(LegalRecordStateService);
  private readonly notificationsService = inject(BENotificationsService);

  protected recordQuantity = 0;
  protected currentRecordInfo = "";
  protected record: LegalRecordUI;
  private notificationId = -1;

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.initializeData();
    this.initNotificationsSuscription();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.notificationsService.unsubscribeToNotifications(this.notificationId);
  }

  private initializeData(): void {
    // Ensure record list is loaded first
    this.legalRecordStateService
      .updateRecordList()
      .pipe(takeUntil(this.destroy$))
      .subscribe({ error: (error: unknown) => processHttpError(error) });

    // Subscribe to record count updates
    this.legalRecordStateService
      .getRecordCount()
      .pipe(takeUntil(this.destroy$))
      .subscribe(count => {
        this.recordQuantity = count;
      });

    this.legalRecordStateService
      .getCurrentRecordInfo()
      .pipe(takeUntil(this.destroy$))
      .subscribe(record => {
        if (record) {
          this.record = record;
          this.currentRecordInfo =
            `${record.recordId ?? ""} ${record.processType ?? ""}`.trim();
        } else {
          this.currentRecordInfo = "";
        }
      });
  }

  goChooseRecords(): void {
    this.router.navigate(["/records"]);
  }

  initNotificationsSuscription() {
    this.notificationId = this.notificationsService.subscribeToNotifications(
      this,
      [
        LCEventEnum.NOTIF_RECORD_DOCUMENT_REQUESTED,
        LCEventEnum.NOTIF_RECORD_DOCUMENT_UPLOADED,
        LCEventEnum.NOTIF_RECORD_DOCUMENT_UPLOAD_FAILED,
        LCEventEnum.NOTIF_RECORD_DOCUMENT_VALIDATED,
        LCEventEnum.NOTIF_RECORD_DOCUMENT_REJECTED,
        LCEventEnum.NOTIF_RECORD_DOCUMENT_DEADLINE_WARNING,
        LCEventEnum.NOTIF_RECORD_ALL_DOCUMENTS_VALIDATED,
        LCEventEnum.NOTIF_RECORD_ACTIVITY_CREATED,
        LCEventEnum.NOTIF_RECORD_ACTIVITY_UPDATED,
        LCEventEnum.NOTIF_RECORD_PAYMENT_REQUESTED,
        LCEventEnum.NOTIF_RECORD_PAYMENT_CREDITED,
        LCEventEnum.NOTIF_RECORD_PAYMENT_RECEIVED,
        LCEventEnum.NOTIF_RECORD_PAYMENT_FAILED,
        LCEventEnum.NOTIF_RECORD_WORK_AUTHORIZED,
        LCEventEnum.NOTIF_RECORD_ALL_DOCUMENTS_VALIDATED,
        LCEventEnum.NOTIF_RECORD_WORK_COMPLETED,
        LCEventEnum.NOTIF_RECORD_DELIVER_RESULTS,
        LCEventEnum.NOTIF_RECORD_RESULTS_DELIVERED,
        LCEventEnum.NOTIF_RECORD_WORK_DEFINED,
        LCEventEnum.NOTIF_RECORD_STATUS_CHANGED,
        LCEventEnum.NOTIF_RECORD_CLOSED,
        LCEventEnum.NOTIF_RECORD_CREATED,
        LCEventEnum.NOTIF_RECORD_LDP_REQUESTED,
      ],
      true
    );
  }

  processNotification(notification: NotificationMessage): void {
    if (!notification.event) {
      return;
    }

    this.legalRecordStateService
      .forceRefreshCurrentRecord()
      .subscribe(record => {
        if (!record) {
          return;
        }

        this.record = record;
      });
  }

  protected get requesterIsNotClient(): boolean {
    return this.record?.requesterIsNotClient ?? false;
  }
}
